import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Video, ExternalLink, Calendar, Clock, Sun, Copy, CheckCircle2, AlertCircle, BookOpen } from 'lucide-react';
import { CHURCH } from '@/api/client';
import SectionTitle from '@/components/SectionTitle';
import Seo from '@/seo/Seo';
import { PAGE_SEO } from '@/seo/seoConfig';
import { breadcrumbSchema } from '@/seo/schemas';
import { toast } from 'sonner';

// Compute next Monday 5:00 AM Europe/Dublin in local time
function nextMorningPrayer() {
  const now = new Date();
  const target = new Date(now);
  target.setHours(5, 0, 0, 0);
  // Find next Monday (1)
  const day = target.getDay();
  let daysUntil = (1 - day + 7) % 7;
  if (daysUntil === 0 && now >= target) daysUntil = 7;
  target.setDate(target.getDate() + daysUntil);
  return target;
}

function formatCountdown(diff) {
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, live: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, live: false };
}

function isPrayerLiveNow() {
  const now = new Date();
  const dow = now.getDay();
  if (dow !== 1) return false;
  const h = now.getHours();
  return h >= 5 && h < 6;
}

export default function MorningPrayer() {
  const [now, setNow] = useState(new Date());
  const [next, setNext] = useState(nextMorningPrayer());
  const [copied, setCopied] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setNow(new Date());
      setNext(nextMorningPrayer());
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Google Meet blocks iframe embedding (X-Frame-Options). Detect after timeout.
  useEffect(() => {
    const t = setTimeout(() => setIframeBlocked(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const diff = next.getTime() - now.getTime();
  const c = formatCountdown(diff);
  const live = isPrayerLiveNow();
  const meetUrl = CHURCH.morningPrayer.meetUrl;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(meetUrl);
      setCopied(true);
      toast.success('Meeting link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy. Long-press to copy manually.');
    }
  };

  const calendarUrl = (() => {
    // Google Calendar quick-add for recurring weekly Monday 5am Dublin
    const start = new Date(next);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const u = new URL('https://calendar.google.com/calendar/render');
    u.searchParams.set('action', 'TEMPLATE');
    u.searchParams.set('text', 'Ministry of Jesus — Early Morning Prayer');
    u.searchParams.set('dates', `${fmt(start)}/${fmt(end)}`);
    u.searchParams.set('details', `Join Early Morning Prayer with Ministry of Jesus.\n\nGoogle Meet: ${meetUrl}\n\n“${CHURCH.motto}” — ${CHURCH.scripture}`);
    u.searchParams.set('location', meetUrl);
    u.searchParams.set('recur', 'RRULE:FREQ=WEEKLY;BYDAY=MO');
    return u.toString();
  })();

  return (
    <div>
      <Seo
        title={PAGE_SEO.morningPrayer.title}
        titleExact
        description={PAGE_SEO.morningPrayer.description}
        keywords={PAGE_SEO.morningPrayer.keywords}
        path="/morning-prayer"
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Morning Prayer', path: '/morning-prayer' }])]}
      />
      <section className="gradient-purple text-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
            <Sun size={14} /> Early Morning Prayer
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">Join us in prayer, before the day begins.</h1>
          <p className="text-lg text-white/90 max-w-3xl">Every Monday from 5:00 AM to 6:00 AM (Ireland time) we gather online to seek the face of God together. Bring your Bible, your requests, and your expectation.</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <a href={meetUrl} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="join-meet-hero">
              <Video size={18}/> Join via Google Meet
            </a>
            <a href={calendarUrl} target="_blank" rel="noopener noreferrer" className="btn-outline !text-white !border-moj-gold hover:!bg-moj-gold hover:!text-moj-purple-deep">
              <Calendar size={18}/> Add to Google Calendar
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 bg-moj-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_360px] gap-8">
          <div>
            <div className="card-moj overflow-hidden mb-6">
              <div className="bg-moj-purple-deep text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Video size={18} className="text-moj-gold"/>
                  <div>
                    <div className="text-sm font-semibold">Google Meet • Morning Prayer</div>
                    <div className="text-xs text-white/70">Code: {CHURCH.morningPrayer.meetCode}</div>
                  </div>
                </div>
                {live && <span className="live-badge">Live Now</span>}
              </div>

              <div className="relative aspect-video bg-gradient-to-br from-moj-purple-deep via-moj-purple to-moj-gold-deep">
                {!iframeBlocked && (
                  <iframe
                    title="Morning Prayer Google Meet"
                    src={meetUrl}
                    className="absolute inset-0 w-full h-full"
                    allow="camera; microphone; fullscreen; display-capture; autoplay"
                    onLoad={() => {/* iframe loaded — may still be blocked by X-Frame-Options */}}
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center text-white text-center px-6">
                  <div className="max-w-md">
                    <Video size={56} className="text-moj-gold mx-auto mb-4"/>
                    <h3 className="font-serif text-2xl mb-2">Tap to enter the prayer room</h3>
                    <p className="text-white/80 text-sm mb-5">
                      Google Meet opens in a new window for the best experience (camera, mic, and screen sharing).
                    </p>
                    <a href={meetUrl} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex" data-testid="join-meet-main">
                      <Video size={18}/> Join Morning Prayer
                    </a>
                    <div className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs text-white/80">
                      <AlertCircle size={12} className="text-moj-gold"/> Embedding restricted by Google — the button above will open the meeting directly.
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-moj-cream-soft flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="font-mono text-moj-purple-deep break-all">{meetUrl}</div>
                <button onClick={copyLink} className="btn-outline text-xs py-1.5 px-3" data-testid="copy-meet-link">
                  {copied ? <><CheckCircle2 size={14}/> Copied</> : <><Copy size={14}/> Copy link</>}
                </button>
              </div>
            </div>

            <div className="card-moj p-6">
              <SectionTitle kicker="How to join" title="Three simple steps" />
              <ol className="space-y-4">
                {[
                  { n: 1, t: 'Click “Join via Google Meet”', d: 'A new Google Meet window will open. You can use any device with a browser — phone, laptop or tablet.' },
                  { n: 2, t: 'Sign in or join as guest', d: 'Sign in with your Google account if you have one. Otherwise enter your name as a guest — the host will admit you.' },
                  { n: 3, t: 'Pray, worship, and stay tender', d: 'Mute your microphone when not speaking. Use “Raise hand” if you’d like to share a testimony or prayer point.' },
                ].map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full gradient-purple flex items-center justify-center text-moj-gold font-bold flex-shrink-0">{s.n}</div>
                    <div>
                      <div className="font-serif text-lg text-moj-purple-deep">{s.t}</div>
                      <p className="text-sm text-moj-charcoal/80">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="card-moj p-6 text-center">
              <div className="text-xs uppercase tracking-widest text-moj-gold-deep font-bold mb-1">{live ? 'Happening now' : 'Next gathering in'}</div>
              {live ? (
                <div className="text-3xl font-serif text-moj-purple-deep my-3">Join us — we’re praying right now.</div>
              ) : (
                <div className="grid grid-cols-4 gap-2 my-4">
                  {[
                    { v: c.d, l: 'days' },
                    { v: c.h, l: 'hours' },
                    { v: c.m, l: 'mins' },
                    { v: c.s, l: 'secs' },
                  ].map((u) => (
                    <div key={u.l} className="bg-moj-cream rounded-lg p-2">
                      <div className="text-2xl font-bold text-moj-purple-deep tabular-nums">{String(u.v).padStart(2, '0')}</div>
                      <div className="text-[10px] uppercase tracking-wider text-moj-gold-deep">{u.l}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-sm text-moj-charcoal/80 mt-2">
                <Clock size={14} className="inline mr-1 text-moj-gold-deep"/> {next.toLocaleString('en-IE', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin' })}
              </div>
              <a href={meetUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full justify-center mt-4"><Video size={16}/> {live ? 'Join now' : 'Join when live'}</a>
            </div>

            <div className="card-moj p-5">
              <h3 className="font-serif text-lg text-moj-purple-deep mb-2">Scripture for the season</h3>
              <p className="italic text-moj-charcoal">“But they that wait upon the LORD shall renew their strength; they shall mount up with wings as eagles.”</p>
              <p className="text-moj-gold-deep font-semibold text-sm mt-2">Isaiah 40:31</p>
            </div>

            <div className="card-moj p-5">
              <h3 className="font-serif text-lg text-moj-purple-deep mb-2 flex items-center gap-2"><BookOpen size={18} className="text-moj-gold-deep"/> What to bring</h3>
              <ul className="text-sm text-moj-charcoal/80 space-y-1 list-disc list-inside">
                <li>Your Bible & a notepad</li>
                <li>A heart prepared to worship</li>
                <li>Your personal prayer requests</li>
                <li>Names of loved ones to lift up</li>
              </ul>
            </div>

            <div className="card-moj p-5">
              <h3 className="font-serif text-lg text-moj-purple-deep mb-2">Need pastoral care?</h3>
              <p className="text-sm text-moj-charcoal/80 mb-3">Reach our prayer team any time.</p>
              <Link to="/contact" className="btn-outline w-full justify-center text-sm">Submit a Prayer Request</Link>
            </div>

            <a href={meetUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-xs text-moj-charcoal/60 hover:text-moj-purple">
              Open meeting directly <ExternalLink size={12}/>
            </a>
          </aside>
        </div>
      </section>
    </div>
  );
}
